import { createStore } from 'vuex'
import XLiteService from '@/services/XLiteService.js'
const debug = require('debug')('xlite-web-client:store')

// TinySimpleHash
const TSH = s => {
    for(var i=0,h=9;i<s.length;)
        h=Math.imul(h^s.charCodeAt(i++),9**9)
    return h^h>>>9
}

const colorsForLightTheme = {
    pinkColor: '#EC407A',
    purpleColor: '#AB47BC',
    deeppurpleColor: '#7E57C2',
    indigoColor: '#5C6BC0',
    blueColor: '#42A5F5',
    lightblueColor: '#29B6F6',
    cyanColor: '#00ACC1',
    tealColor: '#26A69A',
    greenColor: '#66BB6A',
    lightgreenColor: '#9CCC65',
    limeColor: '#D4E157',
    yellowColor: '#FFEE58',
    amberColor: '#FFCA28',
    orangeColor: '#FFA726',
    deeporangeColor: '#FF7043',
    brownColor: '#8D6E63'
}

const colorsForDarkTheme = {
    pinkColor: '#F48FB1',
    purpleColor: '#CE93D8',
    deeppurpleColor: '#B39DDB',
    indigoColor: '#9FA8DA',
    blueColor: '#90CAF9',
    lightblueColor: '#81D4FA',
    cyanColor: '#4DD0E1',
    tealColor: '#80CBC4',
    greenColor: '#A5D6A7',
    lightgreenColor: '#C5E1A5',
    limeColor: '#E6EE9C',
    yellowColor: '#FFF59D',
    amberColor: '#FFE082',
    orangeColor: '#FFCC80',
    deeporangeColor: '#FFAB91',
    brownColor: '#BCAAA4'
}

const setOperator = (state, data) => {
    state.operator.firstName = data.operatorFirstName
    state.operator.lastName = data.operatorLastName
    state.operator.webLogin = data.operatorWebLogin
    state.operatorPreferences.localId = TSH(state.operator.webLogin)
}

const getLocalOperatorObject = () => {
    let op = {}
    if (localStorage.getItem('op')) {
        op = JSON.parse(Buffer.from(localStorage.getItem('op'), 'base64').toString('utf8'))
    }
    return op
}

const addLocalOperatorEntry = (state) => {
    const localId = state.operatorPreferences.localId    
    const op = getLocalOperatorObject()
    if(!Object.prototype.hasOwnProperty.call(op, localId)){
        op[localId]= {}
        localStorage.setItem('op', Buffer.from(JSON.stringify(op)).toString("base64"))
    }
}

const setOperatorPreference = (state, key, value) => {
    const localId = state.operatorPreferences.localId
    const op = getLocalOperatorObject()
    if(localId){
        op[localId][key] = value
        localStorage.setItem('op', Buffer.from(JSON.stringify(op)).toString("base64"))
        state.operatorPreferences[key] = value
    }
}

const initOperatorPreferences = (state) => {
    const localId = state.operatorPreferences.localId
    if(localId){
        const op = getLocalOperatorObject()
        const localOperatorPreferences = op[localId]

        for (const [key, value] of Object.entries(localOperatorPreferences)) {
            state.operatorPreferences[key] = value
        }
    }
}

const setOperatorLayoutMode = (state, mode) => {
    setOperatorPreference(state, 'layoutMode', mode)
}

const setOperatorColorScheme = (state, scheme) => {
    setOperatorPreference(state, 'colorScheme', scheme)
    if(scheme === 'light'){
        state.colors = Object.values(colorsForLightTheme)
        state.reverseColors = Object.values(colorsForLightTheme).reverse()
    } else {
        state.colors = Object.values(colorsForDarkTheme)
        state.reverseColors = Object.values(colorsForDarkTheme).reverse()
    }
}

const setOperatorInputStyle = (state, style) => {
    setOperatorPreference(state, 'inputStyle', style)
}

const setOperatorRippleActive = (state, active) => {
    setOperatorPreference(state, 'rippleActive', active)
}

const setOperatorComponentTheme = (state, theme) => {
    const localId = state.operatorPreferences.localId
    const op = getLocalOperatorObject()
    if(localId){
        op[localId].menuTheme.componentTheme = theme
        state.operatorPreferences.menuTheme.componentTheme = theme
        localStorage.setItem('op', Buffer.from(JSON.stringify(op)).toString("base64"))
    }
}

const setOperatorMenuTheme = (state, menu) => {
    const localId = state.operatorPreferences.localId
    const op = getLocalOperatorObject()
    if(localId){
        op[localId].menuTheme = {...menu}
        state.operatorPreferences.menuTheme.name = menu.name
        state.operatorPreferences.menuTheme.logoColor = menu.logoColor
        state.operatorPreferences.menuTheme.componentTheme = menu.componentTheme
        localStorage.setItem('op', Buffer.from(JSON.stringify(op)).toString("base64"))
    }
}

const setOperatorArticleTableOptionalColumns = (state, columns) => {
    const localId = state.operatorPreferences.localId
    const op = getLocalOperatorObject()
    if(localId){
        var props = Object.getOwnPropertyNames(state.operatorPreferences.articleTableOptionalColumns)
        for (var i = 0; i < props.length; i++) {
            delete state.operatorPreferences.articleTableOptionalColumns[props[i]]
        }
        state.operatorPreferences.articleTableOptionalColumns = { ...columns }
        op[localId].articleTableOptionalColumns = { ...columns }
        localStorage.setItem('op', Buffer.from(JSON.stringify(op)).toString("base64"))
    }
}

const setOperatorArticleTableVisibleRows = (state, rows) => {
    setOperatorPreference(state, 'articleTableVisibleRows', rows)
}

const setOperatorLastSelectedPrinter = (state, printerId) => {
    setOperatorPreference(state, 'lastSelectedPrinter', printerId)
}

const setOperatorYearMonthCompareSemester = (state, value) => {
    setOperatorPreference(state, 'yearMonthCompareSemester', value)
}

const setOperatorBarcodeScannerUseCounterMode = (state, value) => {
    setOperatorPreference(state, 'barcodeScannerUseCounterMode', value)
}

const setOperatorBarcodeScannerUseLoopMode = (state, value) => {
    setOperatorPreference(state, 'barcodeScannerUseLoopMode', value)
}

const setOperatorDashboardFirstSlot = (state, value) => {
    setOperatorPreference(state, 'dashboardFirstSlot', value)
}

const setOperatorDashboardSecondSlot = (state, value) => {
    setOperatorPreference(state, 'dashboardSecondSlot', value)
}

const setOperatorShowPrices = (state, value) => {
    setOperatorPreference(state, 'showPrices', value)
}

const setOperatorShowCosts = (state, value) => {
    setOperatorPreference(state, 'showCosts', value)
}

const setTexp = (state, epoch) => {
    const expiration = new Date(epoch)
    state.at.e = expiration
}

export default createStore({
    state: {
        categoryTree:{
            selectedNodes: {
                categoryId: null,
                subCategoryId: null
            },
            modelValue: {}
        },
        at:{
            a: false,
            t: null,
            e: null
        },
        session:{
            li: false,
            sto: false,
            stt: false,
            opd: false
        },
        operator:{
            firstName: null,
            lastName: null,
            webLogin: null
        },
        operatorPreferences:{
            localId: null,
            layoutMode: null,
            colorScheme: null,
            inputStyle: null,
            rippleActive: null,
            menuTheme: {
                name: null,
                logoColor: null,
                componentTheme: undefined
            },
            articleTableOptionalColumns: {
                
            },
            articleTableVisibleRows: null,
            lastSelectedPrinter: null,
            yearMonthCompareSemester: null,
            barcodeScannerUseCounterMode: null,
            barcodeScannerUseLoopMode: null,
            dashboardFirstSlot: null,
            dashboardSecondSlot: null,
            showPrices: null,
            showCosts: null,
        },
        colors: Object.values(colorsForLightTheme),
        reverseColors: Object.values(colorsForLightTheme).reverse(),
        globalLoading: true,
        globalBlock: false,
        dbPaths: [],
        searchKeyword: '',
        requestedRouterDestination: null
    },
    getters: {
        a: state => {
            return state.at.a
        },
        t: state => {
            return state.at.t
        },
        e: state => {
            return state.at.e
        },
        sto: state => {
            return state.session.sto
        },
        stt: state => {
            return state.session.stt
        },
        opd: state => {
            return state.session.opd
        },
        li: state => {
            return state.session.li
        },
        operator: state => {
            const firstName = state.operator.firstName || ''
            const lastName = state.operator.lastName || ''
            const webLogin = state.operator.webLogin
            if(firstName != '' || lastName != ''){
                return `${firstName} ${lastName}`.trim()
            } else {
                return webLogin
            }
        },
        operatorPreferences: state => {
            return state.operatorPreferences
        },
        articleTableOptionalColumns: state => {
            return state.operatorPreferences.articleTableOptionalColumns
        },
        articleTableVisibleRows: state => {
            return state.operatorPreferences.articleTableVisibleRows
        },
        lastSelectedPrinter: state => {
            return state.operatorPreferences.lastSelectedPrinter
        },
        yearMonthCompareSemester: state => {
            return state.operatorPreferences.yearMonthCompareSemester
        },
        barcodeScannerUseCounterMode: state => {
            return state.operatorPreferences.barcodeScannerUseCounterMode
        },
        barcodeScannerUseLoopMode: state => {
            return state.operatorPreferences.barcodeScannerUseLoopMode
        },
        dashboardFirstSlot: state => {
            return state.operatorPreferences.dashboardFirstSlot
        },
        dashboardSecondSlot: state => {
            return state.operatorPreferences.dashboardSecondSlot
        },
        showPrices: state => {
            return state.operatorPreferences.showPrices
        },
        showCosts: state => {
            return state.operatorPreferences.showCosts
        },
        globalLoading: state => {
            return state.globalLoading
        },
        globalBlock: state => {
            return state.globalBlock
        },
        dbPaths: state => {
            return state.dbPaths
        },
        selectedCategoryTreeNodes: state => {
            return state.categoryTree.selectedNodes
        },
        treeCategory: state => {
            return state.categoryTree.selectedNodes.categoryId
        },
        treeSubcategory: state => {
            return state.categoryTree.selectedNodes.subCategoryId
        },
        categoryTreeModelValue: state => {
            return state.categoryTree.modelValue
        },
        searchKeyword: state => {
            return state.searchKeyword
        },
        colors: state => {
            return state.colors
        },
        reverseColors: state => {
            return state.reverseColors
        },
        requestedRouterDestination: state => {
            return state.requestedRouterDestination
        }
    },
    mutations: {
        SET_A(state, value) {
            state.at.a = value
        },
        SET_T(state, value) {
            state.at.t = value
        },
        SET_E(state, epoch) {
            setTexp(state, epoch)
        },
        RESET_E(state) {
            state.at.e = null
        },
        SET_STO(state, value) {
            state.session.sto = value
        },
        SET_STT(state, value) {
            state.session.stt = value
        },
        SET_OPD(state, value) {
            state.session.opd = value
        },
        SET_LI(state, value) {
            state.session.li = value
        },
        SET_DB_PATHS(state, dbPaths) {
            state.dbPaths = dbPaths
        },
        RESET_DB_PATHS(state) {
            state.dbPaths = []
        },
        SET_OPERATOR(state, data) {
            setOperator(state, data)
            addLocalOperatorEntry(state)
        },
        SET_OPERATOR_LAYOUT_MODE(state, mode)  {
            setOperatorLayoutMode(state, mode)
        },
        SET_OPERATOR_COLOR_SCHEME(state, scheme) {
            setOperatorColorScheme(state, scheme)
        },
        SET_OPERATOR_INPUT_STYLE(state, style) {
            setOperatorInputStyle(state, style)
        },
        SET_OPERATOR_RIPPLE_ACTIVE(state, active) {
            setOperatorRippleActive(state, active)
        },
        SET_OPERATOR_MENU_THEME(state, menu) {
            setOperatorMenuTheme(state, menu)
        },
        SET_OPERATOR_COMPONENT_THEME(state, theme) {
            setOperatorComponentTheme(state, theme)
        },
        SET_OPERATOR_ARTICLE_TABLE_OPTIONAL_COLUMNS(state, columns) {
            setOperatorArticleTableOptionalColumns(state, columns)
        },
        SET_OPERATOR_ARTICLE_TABLE_VISIBLE_ROWS(state, rows) {
            setOperatorArticleTableVisibleRows(state, rows)
        },
        SET_OPERATOR_LAST_SELECTED_PRINTER(state, printerId){
            setOperatorLastSelectedPrinter(state, printerId)
        },
        SET_OPERATOR_YEAR_MONTH_COMPARE_SEMESTER(state, value){
            setOperatorYearMonthCompareSemester(state, value)
        },
        SET_OPERATOR_SCANNER_USE_COUNTER_MODE(state, value){
            setOperatorBarcodeScannerUseCounterMode(state, value)
        },
        SET_OPERATOR_SCANNER_USE_LOOP_MODE(state, value){
            setOperatorBarcodeScannerUseLoopMode(state, value)
        },
        SET_OPERATOR_DASHBOARD_FIRST_SLOT(state, componentName){
            setOperatorDashboardFirstSlot(state, componentName)
        },
        SET_OPERATOR_DASHBOARD_SECOND_SLOT(state, componentName){
            setOperatorDashboardSecondSlot(state, componentName)
        },
        SET_OPERATOR_SHOW_PRICES(state, value){
            setOperatorShowPrices(state, value)
        },
        SET_OPERATOR_SHOW_COSTS(state, value){
            setOperatorShowCosts(state, value)
        },
        RESET_OPERATOR(state) {
            Object.keys(state.operator).forEach(v => state.operator[v] = null)
        },
        RESET_OPERATOR_PREFERENCES(state) {
            Object.keys(state.operatorPreferences).forEach((v) => {
                if(v != 'menuTheme' && v != 'articleTableOptionalColumns'){
                    state.operatorPreferences[v] = null
                } else {
                    if(v == 'menuTheme'){
                        state.operatorPreferences[v] =  {
                            name: null,
                            logoColor: null,
                            componentTheme: undefined
                        }
                    } else {
                        state.operatorPreferences[v] = {}
                    }
                }
            })
        },
        SET_GLOBAL_LOADING(state, value) {
            state.globalLoading = value
        },
        SET_GLOBAL_BLOCK(state, value) {
            state.globalBlock = value
        },
        SET_TREE_CATEGORY(state, id) {
            state.categoryTree.selectedNodes.categoryId = id
        },
        SET_TREE_SUBCATEGORY(state, id) {
            state.categoryTree.selectedNodes.subCategoryId = id
        },
        SET_CATEGORY_TREE_MODELVALUE(state, modelValue) {
            state.categoryTree.modelValue = modelValue
        },
        RESET_TREE_CATEGORY(state) {
            state.categoryTree.modelValue = {}
            state.categoryTree.selectedNodes.categoryId = null
            state.categoryTree.selectedNodes.subCategoryId = null
        },
        SET_SEARCH_KEYWORD(state, keyword) {
            state.searchKeyword = keyword
        },
        SET_REQUESTED_ROUTER_DESTINATION(state, destination) {
            state.requestedRouterDestination = destination
        },
        INITIALISE_OPERATOR_PREFERENCES(state){
            debug('Start op. pref init...', state.at.a)
            if (state.at.a) {
                initOperatorPreferences(state)
            }
        }
    },
    actions: {
        async logout({commit}){
            debug('trying to logout')
            commit('SET_GLOBAL_LOADING', true)
            commit('SET_A', false)
            commit('SET_T', null)
            commit('RESET_DB_PATHS')
            commit('RESET_OPERATOR')
            commit('RESET_OPERATOR_PREFERENCES')
            commit('RESET_TREE_CATEGORY')
            commit('RESET_E')
            commit('SET_SEARCH_KEYWORD', '')
            try {
              const logout = await XLiteService.logout()
              debug('Logged out succesfully', logout.status)
            } catch(err) {
              debug('Impossible to logout', err)
            } finally {
              commit('SET_GLOBAL_LOADING', false)
            }
        },
        setA({ commit }, value) {
            commit('SET_A', value)
        },
        setT({ commit }, value) {
            commit('SET_T', value)
        },
        setE({ commit }, e) {
            const epoch = Number(e)
            if(epoch){
              commit('SET_E', epoch)
            }
        },
        setSto({ commit }, value) {
            return new Promise((resolve) => {
              commit('SET_STO', value)
              resolve()
            })
        },
        setStt({ commit }, value) {
            return new Promise((resolve) => {
              commit('SET_STT', value)
              resolve()
            })
        },
        setOpd({ commit }, value) {
            commit('SET_OPD', value)
        },
        setLi({ commit }, value){
            return new Promise((resolve) => {
              commit('SET_LI', value.li)
              if(value.li == 'lasting'){
                debug(value.e)
                commit('SET_A', true)
              }
              resolve()
            })
        },
        async setDbPaths(context){
            try {
                const response = await XLiteService.getMenu()
                context.commit('SET_DB_PATHS', response.data.paths)
                return context.getters.dbPaths
            } catch(err) {
                debug('getMenu error', err)
                context.commit('RESET_DB_PATHS')
                return context.getters.dbPaths
            }
        },
        setOperator({ commit }, data){
            return new Promise((resolve) => {
              debug(data)
              commit('SET_OPERATOR', data)
              resolve()
            })
        },
        setOperatorLayoutMode({ commit }, mode){
            commit('SET_OPERATOR_LAYOUT_MODE', mode)
        },
        setOperatorColorScheme({ commit }, scheme){
            commit('SET_OPERATOR_COLOR_SCHEME', scheme)
        },
        setOperatorInputStyle({ commit }, style){
            commit('SET_OPERATOR_INPUT_STYLE', style)
        },
        setOperatorRippleActive({ commit }, active){
            commit('SET_OPERATOR_RIPPLE_ACTIVE', active)
        },
        setOperatorMenuTheme({ commit }, menu){
            commit('SET_OPERATOR_MENU_THEME', menu)
        },
        setOperatorComponentTheme({ commit }, theme){
            commit('SET_OPERATOR_COMPONENT_THEME', theme)
        },
        setOperatorArticleTableOptionalColumns({ commit }, columns){
            commit('SET_OPERATOR_ARTICLE_TABLE_OPTIONAL_COLUMNS', columns)
        },
        setOperatorArticleTableVisibleRows({ commit }, rows){
            commit('SET_OPERATOR_ARTICLE_TABLE_VISIBLE_ROWS', rows)
        },
        setOperatorLastSelectedPrinter({ commit }, printerId){
            commit('SET_OPERATOR_LAST_SELECTED_PRINTER', printerId)
        },
        setOperatorYearMonthCompareSemester({ commit }, value){
            commit('SET_OPERATOR_YEAR_MONTH_COMPARE_SEMESTER', value)
        },
        setOperatorBarcodeScannerUseCounterMode({ commit }, value){
            commit('SET_OPERATOR_SCANNER_USE_COUNTER_MODE', value)
        },
        setOperatorBarcodeScannerUseLoopMode({ commit }, value){
            commit('SET_OPERATOR_SCANNER_USE_LOOP_MODE', value)
        },       
        setOperatorDashboardFirstSlot({ commit }, componentName){
            commit('SET_OPERATOR_DASHBOARD_FIRST_SLOT', componentName)
        },
        setOperatorDashboardSecondSlot({ commit }, componentName){
            commit('SET_OPERATOR_DASHBOARD_SECOND_SLOT', componentName)
        },
        setOperatorShowPrices({ commit }, value){
            commit('SET_OPERATOR_SHOW_PRICES', value)
        },
        setOperatorShowCosts({ commit }, value){
            commit('SET_OPERATOR_SHOW_COSTS', value)
        },
        resetDbPaths({ commit }){
            commit('RESET_DB_PATHS')
        },
        enableGlobalLoading({ commit }) {
            commit('SET_GLOBAL_LOADING', true)
        },
        disableGlobalLoading({ commit }) {
            commit('SET_GLOBAL_LOADING', false)
        },
        enableGlobalBlock({ commit }) {
            commit('SET_GLOBAL_BLOCK', true)
        },
        disableGlobalBlock({ commit }) {
            commit('SET_GLOBAL_BLOCK', false)
        },
        setTreeCategory({ commit }, id) {
            commit('SET_TREE_CATEGORY', id)
        },
        setTreeSubcategory({ commit }, id) {
            commit('SET_TREE_SUBCATEGORY', id)
        },
        setCategoryTreeModelValue({ commit }, modelValue){
            commit('SET_CATEGORY_TREE_MODELVALUE', modelValue)
        },
        setSearchKeyword({ commit }, keyword){
            commit('SET_SEARCH_KEYWORD', keyword)
        },
        setRequestedRouterDestination({ commit }, destination){
            commit('SET_REQUESTED_ROUTER_DESTINATION', destination)
        }
    },
    modules: {

    }
})